import { useState, type JSX } from 'react';
import {
  useDecideDoubtMutation,
  useDoubtStatusQuery,
  useRaiseDoubtMutation,
  useUploadDoubtRequirementMutation
} from '@/services/api';
import type { DoubtAction, DoubtDto, DoubtStageEvidence } from '@/types/api';


const MAX_REQUIREMENT_BYTES = 5 * 1024 * 1024;

const REQUIREMENT_ACCEPT = '.docx,.pdf,.md,.txt,.rtf,.xlsx,.xls,.csv';

function uploadErrorMessage(err: unknown): string {
  const e = err as { data?: { errors?: Array<{ message?: string }> } };
  const msg = e?.data?.errors?.[0]?.message;
  return typeof msg === 'string' && msg.length > 0
    ? msg
    : 'Could not read this file — paste the requirement text instead.';
}

const ASSESSMENT_LABEL: Record<DoubtStageEvidence['assessment'], string> = {
  honoured: 'met the requirement',
  failed: 'broke the chain',
  not_applicable: 'not at fault'
};

const STAGE_AGENT: Record<string, string> = {
  explore: 'Explorer',
  plan: 'Architect',
  code: 'Coder',
  review: 'Reviewer',
  test: 'Tester',
  document: 'Documenter'
};

function stageWithAgent(stage: string): string {
  const agent = STAGE_AGENT[stage];
  return agent === undefined ? stage : `${stage} (${agent})`;
}

const ASSESSMENT_CLASS: Record<DoubtStageEvidence['assessment'], string> = {
  honoured: 'bg-ok/15 text-ok border-ok/25',
  failed: 'bg-err/15 text-err border-err/25',
  not_applicable: 'bg-text/[0.05] text-muted border-border'
};

function decideDoubtErrorMessage(err: unknown): string {
  const detail = (err as { data?: { error?: { details?: { reason?: string } } } })?.data?.error
    ?.details?.reason;
  if (detail === 'doubt_already_decided') {
    return 'Someone else already actioned this verdict — reloading shows their decision.';
  }
  if (detail === 'not_assigned_to_stage') {
    return 'You are not assigned to this stage — only an assigned approver or an admin can action it.';
  }
  if (detail === 'role_not_permitted') {
    return 'You do not have permission to action a verdict.';
  }
  if (detail === 'doubt_limit_reached') {
    return 'This approval has reached its limit for feedback rounds.';
  }
  if (detail === 'doubt_in_progress') {
    return 'The investigation is still running.';
  }
  return 'Could not action the verdict. Please try again.';
}

function StatusPill({ status }: { status: DoubtDto['status'] }): JSX.Element {
  const map: Record<DoubtDto['status'], [string, string]> = {
    analysing: ['investigating…', 'bg-warn/15 text-warn'],
    verdict_pending: ['awaiting your decision', 'bg-accent/15 text-accent'],
    resolved: ['applied', 'bg-ok/15 text-ok'],
    declined: ['declined', 'bg-panel-2 text-muted'],
    failed: ['investigation failed', 'bg-err/15 text-err']
  };
  const [label, cls] = map[status];
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-[10px] font-medium ${cls}`}>{label}</span>
  );
}

function RaiseForm({
  approvalId,
  disabled
}: {
  approvalId: string;
  disabled: boolean;
}): JSX.Element {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [raise, result] = useRaiseDoubtMutation();
  const filled = text.trim().length > 0;

  if (!open) {
    return (
      <button
        type="button"
        className="btn"
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid="doubt-open"
      >
        Raise a concern
      </button>
    );
  }
  return (
    <div className="mt-3 p-3 bg-panel-2 rounded-md" data-testid="doubt-form">
      <label className="block text-[12px] text-muted mb-1" htmlFor="doubt-text">
        What looks wrong? <span className="text-err">*</span>
      </label>
      <textarea
        id="doubt-text"
        className="w-full bg-panel border border-border rounded-md p-2 text-[13px]"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="e.g. why was the export built as CSV when the requirement asked for Excel?"
      />
      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          className="btn"
          disabled={!filled || result.isLoading}
          onClick={() => {
            void raise({ approval_id: approvalId, text: text.trim() });
          }}
          data-testid="doubt-submit"
        >
          {result.isLoading ? 'Investigating…' : 'Investigate this'}
        </button>
        <button type="button" className="btn ghost" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
      {result.isError ? (
        <div className="text-[12px] text-err mt-2">{decideDoubtErrorMessage(result.error)}</div>
      ) : null}
    </div>
  );
}

function VerdictView({
  approvalId,
  doubt
}: {
  approvalId: string;
  doubt: DoubtDto;
}): JSX.Element {
  const [decide, result] = useDecideDoubtMutation();
  const [reason, setReason] = useState('');
  const [requirementText, setRequirementText] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const [upload, uploadState] = useUploadDoubtRequirementMutation();
  const [confirmingRestart, setConfirmingRestart] = useState(false);
  const [chosen, setChosen] = useState<DoubtAction | null>(null);
  const verdict = doubt.verdict;
  const filled = reason.trim().length > 0;
  const culprit = verdict?.culprit_stage ?? null;

  const permitted =
    doubt.permitted_actions ??
    (verdict?.outcome === 'not_in_requirements'
      ? (['supply_requirement', 'decline_continue', 'decline_fail'] as DoubtAction[])
      : (['reroute', 'decline_continue', 'decline_fail'] as DoubtAction[]));
  const requirementFilled = requirementText.trim().length > 0;

  const CHOICE_COPY: Record<
    DoubtAction,
    { title: string; consequence: string; confirm: string; danger?: boolean; testid: string; confirm_testid: string }
  > = {
    reroute: {
      title:
        culprit !== null
          ? `Fix it — re-run from ${stageWithAgent(culprit)}`
          : 'Fix it — re-run the pipeline',
      consequence:
        culprit !== null
          ? `The ${culprit} stage reworks its output against your feedback; every later stage runs again from scratch. No other run is affected.`
          : 'The pipeline runs again from the stage at fault.',
      confirm: culprit !== null ? `Re-run from ${stageWithAgent(culprit)}` : 'Re-run the pipeline',
      testid: 'doubt-choice-reroute',
      confirm_testid: 'doubt-reroute'
    },
    supply_requirement: {
      title: 'Add the missing requirement and start again',
      consequence:
        'The pipeline restarts from the beginning using your corrected requirement. Work already approved is discarded, because the brief it was judged against has changed.',
      confirm: 'Continue',
      testid: 'doubt-choice-supply',
      confirm_testid: 'doubt-supply-submit'
    },
    decline_continue: {
      title: 'Accept the output — approve and continue',
      consequence:
        'You disagree with the finding, or it does not matter here. This approves the stage and the run moves on.',
      confirm: 'Approve and continue',
      testid: 'doubt-choice-continue',
      confirm_testid: 'doubt-decline-continue'
    },
    decline_fail: {
      title: 'Stop this run',
      consequence: 'The run ends here and is marked failed. Nothing further executes.',
      confirm: 'Stop this run',
      danger: true,
      testid: 'doubt-choice-fail',
      confirm_testid: 'doubt-decline-fail'
    }
  };

  const ORDER: DoubtAction[] = ['reroute', 'supply_requirement', 'decline_continue', 'decline_fail'];
  const choices = ORDER.filter(
    (a) => permitted.includes(a) && !(a === 'reroute' && culprit === null)
  ).map((a) => ({ action: a, ...CHOICE_COPY[a] }));
  const chosenChoice = chosen !== null ? CHOICE_COPY[chosen] : undefined;

  const act = (action: DoubtAction): void => {
    if (!filled) return;
    void decide({ approval_id: approvalId, doubt_id: doubt.doubt_id, action, reason: reason.trim() });
  };

  const submitRequirement = (): void => {
    if (!filled || !requirementFilled) return;
    void decide({
      approval_id: approvalId,
      doubt_id: doubt.doubt_id,
      action: 'supply_requirement',
      reason: reason.trim(),
      requirement_text: requirementText.trim()
    });
    setConfirmingRestart(false);
  };

  const onFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setUploadError(null);
    setUploadedName(null);
    if (file.size > MAX_REQUIREMENT_BYTES) {
      setUploadError('File exceeds the 5 MB limit — paste the requirement text instead.');
      return;
    }
    try {
      const res = await upload({ approvalId, file }).unwrap();
      setRequirementText((prev) =>
        prev.trim().length > 0 ? `${prev.trim()}\n\n${res.extracted_text}` : res.extracted_text
      );
      setUploadedName(file.name);
    } catch (e) {
      setUploadError(uploadErrorMessage(e));
    }
  };

  return (
    <div className="mt-3" data-testid="doubt-verdict">
      {verdict ? (
        <>
          <div className="text-[13px] mb-2">{verdict.summary}</div>
          <div className="text-[11px] text-muted mb-3">
            confidence: {verdict.confidence}
            {verdict.notes ? ` · ${verdict.notes}` : ''}
          </div>

          <div className="mb-3" data-testid="doubt-evidence">
            {verdict.stage_evidence.map((e) => (
              <details
                key={e.stage}
                className="group border-b border-border/60 last:border-0"
                data-testid={`doubt-evidence-${e.stage}`}
              >
                <summary className="cursor-pointer list-none select-none flex gap-3 items-center py-1.5 px-2 -mx-2 rounded-md hover:bg-panel-2">
                  <span className="text-muted text-[11px] w-3 shrink-0 text-center leading-none transition-transform group-open:rotate-90">
                    ›
                  </span>
                  <span className="font-mono text-[12px] w-[72px] shrink-0">{e.stage}</span>
                  <span
                    className={`text-[11px] leading-[18px] px-2 py-[1px] rounded-[10px] shrink-0 w-[136px] text-center border ${ASSESSMENT_CLASS[e.assessment]}`}
                  >
                    {ASSESSMENT_LABEL[e.assessment]}
                  </span>
                  {STAGE_AGENT[e.stage] !== undefined ? (
                    <span className="text-[11px] text-muted shrink-0">
                      {STAGE_AGENT[e.stage]}
                    </span>
                  ) : null}
                </summary>
                <div className="text-[12px] text-muted leading-relaxed pl-[calc(0.75rem+0.75rem+72px)] pb-2.5 pr-2">
                  {e.what_it_produced}
                  {e.why ? ` — ${e.why}` : ''}
                </div>
              </details>
            ))}
          </div>

          {culprit !== null ? (
            <div className="text-[13px] font-medium mb-3" data-testid="doubt-recommendation">
              The <strong>{culprit}</strong> stage
              {STAGE_AGENT[culprit] !== undefined ? ` (${STAGE_AGENT[culprit]})` : ''} is where this
              went wrong.
            </div>
          ) : (
            <div className="text-[13px] font-medium mb-3" data-testid="doubt-recommendation">
              {verdict.outcome === 'not_in_requirements'
                ? 'This was never part of the requirements, so no stage is at fault.'
                : 'No stage is at fault — every stage met the requirement.'}
            </div>
          )}

          {chosen === null ? (
            <div data-testid="doubt-choices">
              <div className="text-[13px] font-medium mb-2">What would you like to do?</div>
              <div className="flex flex-col gap-2">
                {choices.map((c) => (
                  <button
                    key={c.action}
                    type="button"
                    onClick={() => setChosen(c.action)}
                    data-testid={c.testid}
                    className={`text-left p-3 rounded-md border transition-colors ${
                      c.danger
                        ? 'border-err/40 hover:border-err bg-panel-2'
                        : 'border-border hover:border-text bg-panel-2'
                    }`}
                  >
                    <div
                      className={`text-[13px] font-medium ${c.danger ? 'text-err' : 'text-text'}`}
                    >
                      {c.title}
                    </div>
                    <div className="text-[11.5px] text-muted mt-0.5">{c.consequence}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="doubt-chosen">
              <button
                type="button"
                className="text-[12px] text-muted hover:text-text mb-2"
                onClick={() => setChosen(null)}
                data-testid="doubt-back"
              >
                ← Choose something else
              </button>
              <div className="p-3 bg-panel-2 rounded-md">
                <div
                  className={`text-[13px] font-medium ${chosenChoice?.danger ? 'text-err' : ''}`}
                >
                  {chosenChoice?.title}
                </div>
                <div className="text-[11.5px] text-muted mt-0.5 mb-3">
                  {chosenChoice?.consequence}
                </div>

                {chosen === 'supply_requirement' ? (
                  <div className="mb-3" data-testid="doubt-supply-requirement">
                    <label
                      className="block text-[12px] text-muted mb-1"
                      htmlFor="doubt-requirement-text"
                    >
                      Missing requirement <span className="text-err">*</span>
                    </label>
                    <textarea
                      id="doubt-requirement-text"
                      className="w-full bg-panel border border-border rounded-md p-2 text-[13px]"
                      rows={5}
                      value={requirementText}
                      onChange={(e) => setRequirementText(e.target.value)}
                      placeholder="e.g. The export must be produced as an Excel workbook, with one sheet per region."
                    />
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <label className="btn ghost cursor-pointer" htmlFor="doubt-requirement-file">
                        {uploadState.isLoading ? 'Reading…' : 'Attach a document'}
                      </label>
                      <input
                        id="doubt-requirement-file"
                        type="file"
                        className="hidden"
                        accept={REQUIREMENT_ACCEPT}
                        data-testid="doubt-requirement-file"
                        onChange={(e) => {
                          void onFile(e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                      <span className="text-[11px] text-muted">
                        Word, PDF, Excel, CSV, Markdown, text or RTF · up to 5 MB
                      </span>
                    </div>
                    {uploadedName !== null ? (
                      <div
                        className="text-[11px] text-ok mt-2"
                        data-testid="doubt-requirement-attached"
                      >
                        Added the text from {uploadedName} — edit it above before submitting if you
                        need to.
                      </div>
                    ) : null}
                    {uploadError !== null ? (
                      <div
                        className="text-[12px] text-err mt-2"
                        data-testid="doubt-requirement-upload-error"
                      >
                        {uploadError}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <label className="block text-[12px] text-muted mb-1" htmlFor="doubt-decide-reason">
                  Reason <span className="text-err">*</span>
                </label>
                <input
                  id="doubt-decide-reason"
                  className="w-full bg-panel border border-border rounded-md p-2 text-[13px] mb-3"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />

                <button
                  type="button"
                  className={chosenChoice?.danger ? 'btn danger' : 'btn'}
                  disabled={
                    !filled ||
                    result.isLoading ||
                    (chosen === 'supply_requirement' && !requirementFilled)
                  }
                  onClick={() => {
                    if (chosen === 'supply_requirement') setConfirmingRestart(true);
                    else act(chosen);
                  }}
                  data-testid={chosenChoice?.confirm_testid}
                >
                  {result.isLoading ? 'Working…' : chosenChoice?.confirm}
                </button>
              </div>
            </div>
          )}

          {confirmingRestart ? (
            <div
              className="mt-3 p-3 border border-warn rounded-md bg-warn/10"
              data-testid="doubt-restart-warning"
            >
              <div className="text-[13px] font-medium mb-1">
                This restarts the pipeline from the beginning.
              </div>
              <div className="text-[12px] text-muted mb-3">
                Every stage runs again with the corrected requirement, including stages that were
                already approved — their output is discarded and rebuilt, because the requirement
                they were checked against has changed. The original requirement is kept on record
                alongside your correction. No other run is affected.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn danger"
                  disabled={result.isLoading}
                  onClick={submitRequirement}
                  data-testid="doubt-restart-confirm"
                >
                  {result.isLoading ? 'Restarting…' : 'Discard and restart from the beginning'}
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setConfirmingRestart(false)}
                  data-testid="doubt-restart-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
          {result.isError ? (
            <div className="text-[12px] text-err mt-2">{decideDoubtErrorMessage(result.error)}</div>
          ) : null}
        </>
      ) : (
        <div className="text-[12px] text-muted">The verdict is not readable.</div>
      )}
    </div>
  );
}

export function DoubtPanel({
  approvalId,
  canDecide
}: {
  approvalId: string;
  canDecide: boolean;
}): JSX.Element | null {
  const { data } = useDoubtStatusQuery(approvalId, {
    pollingInterval: 5000,
    refetchOnMountOrArgChange: true
  });
  const doubt = data?.doubt ?? null;

  if (!canDecide) return null;

  const inFlight = doubt !== null && (doubt.status === 'analysing' || doubt.status === 'verdict_pending');

  return (
    <section className="mt-4 pt-4 border-t border-border" data-testid="doubt-panel">
      <div className="flex items-center gap-3 mb-2">
        <div className="font-semibold text-[13px]">Feedback</div>
        {doubt ? <StatusPill status={doubt.status} /> : null}
      </div>

      {doubt ? (
        <div className="text-[12px] text-muted mb-2" data-testid="doubt-text-echo">
          “{doubt.text}”
        </div>
      ) : null}

      {doubt?.status === 'analysing' ? (
        <div className="flex items-center gap-2 text-[13px]" data-testid="doubt-analysing">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          Investigating this feedback against the project&apos;s requirements…
          <span className="text-[11px] text-muted">Approve and Deny are paused meanwhile.</span>
        </div>
      ) : null}

      {doubt?.status === 'verdict_pending' ? (
        <VerdictView approvalId={approvalId} doubt={doubt} />
      ) : null}

      {doubt?.status === 'failed' ? (
        <div className="text-[12px] text-err" data-testid="doubt-failed">
          The investigation could not complete
          {doubt.failure_reason ? `: ${doubt.failure_reason}` : ''}. You can approve, stop the run,
          or raise the concern again.
        </div>
      ) : null}

      {doubt?.status === 'declined' ? (
        <div className="text-[12px] text-muted">
          {doubt.action === 'decline_fail'
            ? 'This run was stopped. Nothing further will execute.'
            : 'The output was accepted and this stage approved — the run continues.'}
        </div>
      ) : null}

      {doubt?.status === 'resolved' ? (
        <div className="text-[12px] text-muted">
          The decision was applied
          {doubt.action === 'reroute'
            ? ' — the pipeline is re-running from the stage at fault.'
            : doubt.action === 'supply_requirement'
              ? ' — the requirement was added and the pipeline is running again from the start.'
              : '.'}
        </div>
      ) : null}

      {!inFlight ? <div className="mt-2"><RaiseForm approvalId={approvalId} disabled={false} /></div> : null}
    </section>
  );
}
